import isNil from "lodash/isNil";
import { MatchingRelationType } from "PFApp/constants/matching_relation_type";
import { PER_PAGE_UNPAGINATED } from "PFApp/constants/unpaginated";
import api from "PFCore/api";
import { Collection, Match, Shortlist, ShortlistMinimized, ShortlistSuggested } from "PFTypes";
import { Id } from "PFTypes/id";

export type ShortlistCollection = Collection<Shortlist[]>;
export type RankedShortlistCollection = Collection<Match[]>;

export const fetchShortlists = (activityId, params): Promise<ShortlistCollection> =>
  api({
    url: "shortlists",
    method: "GET",
    params: {
      activityId,
      ...params,
      page: params.page ?? 1,
      perPage: params.per_page ?? PER_PAGE_UNPAGINATED
    }
  });

export const fetchShortlistsRanked = (
  activityId: Id,
  profileIds: Id[] = [],
  page = 1,
  perPage = PER_PAGE_UNPAGINATED,
  relationType?: MatchingRelationType
): Promise<RankedShortlistCollection> =>
  api({
    url: "shortlists/ranked_profiles",
    method: "GET",
    params: {
      activityId,
      ...(profileIds.length ? { profileIds } : {}),
      ...(relationType ? { relationType } : {}),
      page,
      perPage
    }
  });

export const fetchSuspendedShortlists = (
  activityId: Id,
  page = 1,
  perPage = PER_PAGE_UNPAGINATED
): Promise<ShortlistCollection> =>
  api({
    url: "shortlists/suspended_profiles",
    method: "GET",
    params: {
      activityId,
      page,
      perPage
    }
  });

export const newShortlist = (
  activityId: Id,
  profileId: Id,
  named: boolean,
  suggested: ShortlistSuggested | undefined
) =>
  api({
    url: "shortlists",
    method: "post",
    data: {
      profileId,
      activityId,
      named,
      ...(!isNil(suggested) && { suggested })
    }
  });

export const deleteShortlist = (shortlistId) =>
  api({
    url: `shortlists/${shortlistId}`,
    method: "delete"
  });

export type ShortlistToUpdatePick<T extends keyof Shortlist> = {
  [P in T]?: Shortlist[P] | null;
};
export type ShortlistUpdatePayload = ShortlistToUpdatePick<"suggested">;

export const updateShortlist = (shortlistId: Id, data: ShortlistUpdatePayload) =>
  api({
    url: `shortlists/${shortlistId}`,
    method: "PUT",
    data
  });

export const assignShortlist = (shortlistId) =>
  api({
    url: `shortlists/${shortlistId}/assign`,
    method: "put",
    data: {
      shortlistId
    }
  });

export const resetShortlist = (shortlistId) =>
  api({
    url: `shortlists/${shortlistId}/reset`,
    method: "put",
    data: {
      shortlistId
    }
  });

export const acceptShortlist = (shortlistId, data = {}) =>
  api({
    url: `shortlists/${shortlistId}/accept`,
    method: "put",
    data
  });

export const declineShortlist = (shortlistId, data = {}) =>
  api({
    url: `shortlists/${shortlistId}/decline`,
    method: "PUT",
    data
  });

export const fillShortlist = (
  shortlistId: Id,
  { approverId, requestReason, vacancyId }: { approverId?: Id; requestReason?: string; vacancyId?: Id } = {}
) =>
  api<ShortlistMinimized>({
    url: `shortlists/${shortlistId}/fill`,
    method: "put",
    data: {
      approverId,
      requestReason,
      vacancyId
    }
  });

export type ShortlistBookingDates = { startDate: string };

type FillAndBookShortistOptions = {
  skipBookingsCreation?: boolean;
  approverId?: number;
  requestReason?: string;
  vacancyId?: number;
  bookings?: ShortlistBookingDates[];
};

export const fillAndBookShortlist = (shortlistId: Id, data?: FillAndBookShortistOptions) =>
  api<ShortlistMinimized>({
    url: `shortlists/${shortlistId}/fill_and_book`,
    method: "put",
    data
  });

export const bookShortlist = (
  shortlistId: Id,
  skipBookingsCreation?: boolean,
  bookings?: ShortlistBookingDates[]
): Promise<ShortlistMinimized> =>
  api({
    url: `shortlists/${shortlistId}/book`,
    method: "PUT",
    data: {
      skipBookingsCreation,
      bookings
    }
  });

export const rejectShortlist = (shortlistId) =>
  api({
    url: `shortlists/${shortlistId}/reject`,
    method: "put"
  });

export const confirmShortlist = (shortlistIds: number[]) =>
  api({
    url: "/shortlists/bulk_confirm",
    method: "POST",
    data: {
      shortlistIds
    }
  });
